import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import AdminLayout from '../components/AdminLayout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Textarea } from '../components/ui/textarea';
import {
  Shield, Search, Plus, Trash2, Upload, Download, Loader2, X,
  AlertTriangle, CheckCircle, XCircle, Mail, UserX
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AdminSuppression = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [items, setItems] = useState([]);
  const [stats, setStats] = useState(null);
  const [total, setTotal] = useState(0);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterReason, setFilterReason] = useState('');
  const [showAddModal, setShowAddModal] = useState(false);
  const [showBulkModal, setShowBulkModal] = useState(false);
  const [page, setPage] = useState(0);
  const limit = 25;

  useEffect(() => {
    const token = localStorage.getItem('admin_token');
    if (!token) {
      navigate('/admin/login');
      return;
    }
    fetchData();
  }, [navigate, searchQuery, filterReason, page]);

  const fetchData = async () => {
    try {
      setLoading(true);
      
      const params = new URLSearchParams();
      if (searchQuery) params.append('search', searchQuery);
      if (filterReason) params.append('reason', filterReason);
      params.append('skip', page * limit);
      params.append('limit', limit);
      
      const [listRes, statsRes] = await Promise.all([
        fetch(`${API_URL}/api/settings/suppression?${params}`),
        fetch(`${API_URL}/api/settings/suppression/stats`)
      ]);

      if (listRes.ok) {
        const data = await listRes.json();
        setItems(data.items);
        setTotal(data.total);
      }
      if (statsRes.ok) {
        setStats(await statsRes.json());
      }
    } catch (error) {
      console.error('Failed to fetch suppression list:', error);
      toast.error('Failed to load suppression list');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (email) => {
    if (!window.confirm(`Remove ${email} from suppression list?`)) return;

    try {
      const response = await fetch(`${API_URL}/api/settings/suppression/${encodeURIComponent(email)}`, {
        method: 'DELETE'
      });

      if (response.ok) {
        toast.success('Email removed from suppression list');
        fetchData();
      } else {
        toast.error('Failed to remove email');
      }
    } catch (error) {
      toast.error('Failed to remove email');
    }
  };

  const handleExport = () => {
    if (items.length === 0) {
      toast.info('No emails to export');
      return;
    }

    const csv = [
      'email,reason,notes,added_at',
      ...items.map(item => 
        `${item.email},${item.reason},${item.notes || ''},${item.added_at}`
      )
    ].join('\n');

    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `suppression_list_${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success('Suppression list exported');
  };

  const reasonColors = {
    manual: 'bg-gray-100 text-gray-700',
    unsubscribe: 'bg-amber-100 text-amber-700',
    bounce: 'bg-red-100 text-red-700',
    complaint: 'bg-red-100 text-red-700'
  };

  const reasonIcons = {
    manual: UserX,
    unsubscribe: Mail,
    bounce: XCircle,
    complaint: AlertTriangle
  };

  return (
    <AdminLayout>
      <div className="p-6">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-6">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Suppression List</h1>
            <p className="text-gray-600">Manage emails excluded from campaigns</p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={handleExport}>
              <Download className="w-4 h-4 mr-2" />
              Export CSV
            </Button>
            <Button variant="outline" onClick={() => setShowBulkModal(true)}>
              <Upload className="w-4 h-4 mr-2" />
              Bulk Import
            </Button>
            <Button onClick={() => setShowAddModal(true)} className="bg-[#00457f] hover:bg-[#003461]">
              <Plus className="w-4 h-4 mr-2" />
              Add Email
            </Button>
          </div>
        </div>

        {/* Stats */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
            <Card>
              <CardContent className="py-4">
                <div className="text-2xl font-bold text-[#00457f]">{stats.total}</div>
                <div className="text-sm text-gray-500">Total Suppressed</div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="py-4">
                <div className="text-2xl font-bold text-gray-600">{stats.by_reason?.manual || 0}</div>
                <div className="text-sm text-gray-500">Manual</div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="py-4">
                <div className="text-2xl font-bold text-amber-600">{stats.by_reason?.unsubscribe || 0}</div>
                <div className="text-sm text-gray-500">Unsubscribed</div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="py-4">
                <div className="text-2xl font-bold text-red-600">{stats.by_reason?.bounce || 0}</div>
                <div className="text-sm text-gray-500">Bounced</div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="py-4">
                <div className="text-2xl font-bold text-red-600">{stats.by_reason?.complaint || 0}</div>
                <div className="text-sm text-gray-500">Complaints</div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Filters */}
        <div className="flex flex-col md:flex-row gap-4 mb-6">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
            <Input
              placeholder="Search by email..."
              value={searchQuery}
              onChange={(e) => { setSearchQuery(e.target.value); setPage(0); }}
              className="pl-10"
            />
          </div>
          <select
            value={filterReason}
            onChange={(e) => { setFilterReason(e.target.value); setPage(0); }}
            className="px-3 py-2 border rounded-md"
          >
            <option value="">All Reasons</option>
            <option value="manual">Manual</option>
            <option value="unsubscribe">Unsubscribe</option>
            <option value="bounce">Bounce</option>
            <option value="complaint">Complaint</option>
          </select>
        </div>

        {/* List */}
        <Card>
          <CardContent className="p-0">
            {loading ? (
              <div className="flex items-center justify-center h-64">
                <Loader2 className="w-8 h-8 animate-spin text-[#00457f]" />
              </div>
            ) : items.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-64 text-gray-500">
                <Shield className="w-12 h-12 mb-4 text-gray-300" />
                <p>No suppressed emails found</p>
                {(searchQuery || filterReason) && (
                  <Button 
                    variant="link" 
                    onClick={() => { setSearchQuery(''); setFilterReason(''); }}
                    className="mt-2"
                  >
                    Clear filters
                  </Button>
                )}
              </div>
            ) : (
              <>
                <table className="w-full">
                  <thead>
                    <tr className="border-b bg-gray-50">
                      <th className="text-left px-4 py-3 font-medium text-gray-600">Email</th>
                      <th className="text-left px-4 py-3 font-medium text-gray-600">Reason</th>
                      <th className="text-left px-4 py-3 font-medium text-gray-600">Notes</th>
                      <th className="text-left px-4 py-3 font-medium text-gray-600">Added</th>
                      <th className="text-right px-4 py-3 font-medium text-gray-600">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {items.map((item, index) => {
                      const ReasonIcon = reasonIcons[item.reason] || Shield;
                      return (
                        <tr key={index} className="border-b last:border-0 hover:bg-gray-50">
                          <td className="px-4 py-3">
                            <span className="font-medium text-gray-900">{item.email}</span>
                          </td>
                          <td className="px-4 py-3">
                            <span className={`inline-flex items-center gap-1 px-2 py-1 rounded text-xs font-medium capitalize ${reasonColors[item.reason]}`}>
                              <ReasonIcon className="w-3 h-3" />
                              {item.reason}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-500 max-w-xs truncate">
                            {item.notes || '-'}
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-500">
                            {new Date(item.added_at).toLocaleDateString()}
                          </td>
                          <td className="px-4 py-3 text-right">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleDelete(item.email)}
                              className="text-red-600 hover:text-red-700 hover:bg-red-50"
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>

                {/* Pagination */}
                {total > limit && (
                  <div className="flex items-center justify-between px-4 py-3 border-t bg-gray-50">
                    <span className="text-sm text-gray-500">
                      Showing {page * limit + 1} - {Math.min((page + 1) * limit, total)} of {total}
                    </span>
                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setPage(p => Math.max(0, p - 1))}
                        disabled={page === 0}
                      >
                        Previous
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setPage(p => p + 1)}
                        disabled={(page + 1) * limit >= total}
                      >
                        Next
                      </Button>
                    </div>
                  </div>
                )}
              </>
            )}
          </CardContent>
        </Card>

        {/* Add Email Modal */}
        {showAddModal && (
          <AddEmailModal
            onClose={() => setShowAddModal(false)}
            onAdd={() => { setShowAddModal(false); fetchData(); }}
          />
        )}

        {/* Bulk Import Modal */}
        {showBulkModal && (
          <BulkImportModal
            onClose={() => setShowBulkModal(false)}
            onImport={() => { setShowBulkModal(false); fetchData(); }}
          />
        )}
      </div>
    </AdminLayout>
  );
};

const AddEmailModal = ({ onClose, onAdd }) => {
  const [email, setEmail] = useState('');
  const [reason, setReason] = useState('manual');
  const [notes, setNotes] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async () => {
    if (!email.trim()) {
      toast.error('Email is required');
      return;
    }

    try {
      setLoading(true);
      const params = new URLSearchParams({
        email: email.trim(),
        reason,
        notes
      });
      
      const response = await fetch(`${API_URL}/api/settings/suppression?${params}`, {
        method: 'POST'
      });

      if (response.ok) {
        toast.success('Email added to suppression list');
        onAdd();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to add email');
      }
    } catch (error) {
      toast.error('Failed to add email');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
        <div className="p-6">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-lg font-bold text-gray-900">Add to Suppression List</h2>
            <button onClick={onClose} className="text-gray-500 hover:text-gray-700">
              <X className="w-5 h-5" />
            </button>
          </div>

          <div className="space-y-4">
            <div>
              <Label>Email Address</Label>
              <Input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="email@example.com"
              />
            </div>

            <div>
              <Label>Reason</Label>
              <select
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                className="w-full px-3 py-2 border rounded-md"
              >
                <option value="manual">Manual Block</option>
                <option value="unsubscribe">Unsubscribe Request</option>
                <option value="bounce">Email Bounce</option>
                <option value="complaint">Spam Complaint</option>
              </select>
            </div>

            <div>
              <Label>Notes (Optional)</Label>
              <Input
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Reason for adding..."
              />
            </div>
          </div>

          <div className="flex justify-end gap-2 mt-6">
            <Button variant="outline" onClick={onClose}>Cancel</Button>
            <Button onClick={handleSubmit} disabled={loading} className="bg-[#00457f] hover:bg-[#003461]">
              {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Plus className="w-4 h-4 mr-2" />}
              Add Email
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

const BulkImportModal = ({ onClose, onImport }) => {
  const [emails, setEmails] = useState('');
  const [reason, setReason] = useState('manual');
  const [loading, setLoading] = useState(false);
  const fileInputRef = useRef(null);

  const handleFileUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (event) => {
      const text = event.target?.result;
      if (typeof text === 'string') {
        // Extract emails from CSV (handle various formats)
        const lines = text.split('\n');
        const extractedEmails = lines
          .map(line => {
            const parts = line.split(',');
            // Try to find email in each column
            for (const part of parts) {
              const cleaned = part.trim().toLowerCase();
              if (cleaned.includes('@') && cleaned.includes('.')) {
                return cleaned.replace(/['"]/g, '');
              }
            }
            return null;
          })
          .filter(Boolean);
        
        setEmails(extractedEmails.join('\n'));
      }
    };
    reader.readAsText(file);
  };

  const handleImport = async () => {
    const emailList = emails
      .split('\n')
      .map(e => e.trim().toLowerCase())
      .filter(e => e.includes('@') && e.includes('.'));

    if (emailList.length === 0) {
      toast.error('No valid emails to import');
      return;
    }

    try {
      setLoading(true);
      const response = await fetch(`${API_URL}/api/settings/suppression/bulk?reason=${reason}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(emailList)
      });

      if (response.ok) {
        const result = await response.json();
        toast.success(`Added ${result.added} emails (${result.skipped} already existed)`);
        onImport();
      } else {
        toast.error('Import failed');
      }
    } catch (error) {
      toast.error('Import failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-lg w-full">
        <div className="p-6">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-lg font-bold text-gray-900">Bulk Import</h2>
            <button onClick={onClose} className="text-gray-500 hover:text-gray-700">
              <X className="w-5 h-5" />
            </button>
          </div>

          <div className="space-y-4">
            <div>
              <Label>Import from CSV</Label>
              <input
                ref={fileInputRef}
                type="file"
                accept=".csv,.txt"
                onChange={handleFileUpload}
                className="hidden"
              />
              <Button
                variant="outline"
                onClick={() => fileInputRef.current?.click()}
                className="w-full"
              >
                <Upload className="w-4 h-4 mr-2" />
                Upload CSV File
              </Button>
            </div>

            <div className="text-center text-sm text-gray-500">or paste emails below</div>

            <div>
              <Label>Email Addresses (one per line)</Label>
              <Textarea
                value={emails}
                onChange={(e) => setEmails(e.target.value)}
                placeholder="email1@example.com&#10;email2@example.com&#10;email3@example.com"
                rows={8}
              />
              <p className="text-xs text-gray-500 mt-1">
                {emails.split('\n').filter(e => e.includes('@')).length} valid emails detected
              </p>
            </div>

            <div>
              <Label>Reason for All</Label>
              <select
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                className="w-full px-3 py-2 border rounded-md"
              >
                <option value="manual">Manual Block</option>
                <option value="unsubscribe">Unsubscribe Request</option>
                <option value="bounce">Email Bounce</option>
                <option value="complaint">Spam Complaint</option>
              </select>
            </div>
          </div>

          <div className="flex justify-end gap-2 mt-6">
            <Button variant="outline" onClick={onClose}>Cancel</Button>
            <Button onClick={handleImport} disabled={loading} className="bg-[#00457f] hover:bg-[#003461]">
              {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Upload className="w-4 h-4 mr-2" />}
              Import Emails
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminSuppression;
